import React from "react";
import { useAuth } from "../auth";

type Comment = {
  id: string;
  author: string;
  text: string;
  website?: string;
  createdAt: string; // ISO
};

type Props = {
  postId: string;
  initialComments?: Comment[];
  editMode?: boolean;
  adminPw?: string;
  defaultOpen?: boolean;
};

const Comments: React.FC<Props> = ({ postId, initialComments = [], editMode = false, adminPw = "", defaultOpen = false }) => {
  const { isLoggedIn, token } = useAuth();
  const [open, setOpen] = React.useState(!!defaultOpen);
  const [loading, setLoading] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [comments, setComments] = React.useState<Comment[]>(initialComments);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);

  const [author, setAuthor] = React.useState("");
  const [website, setWebsite] = React.useState("");
  const [text, setText] = React.useState("");
  // Honeypot field
  const [honeypot, setHoneypot] = React.useState("");

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/blog/${encodeURIComponent(postId)}/comments`);
        if (res.ok) {
          const json = await res.json();
          if (!cancelled && json && Array.isArray(json.comments)) {
            setComments(json.comments);
          }
        }
      } catch (e) {
        // ignore network errors; keep existing comments
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [open, postId]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const a = author.trim();
    const t = text.trim();
    const w = website.trim();
    if (a.length === 0 || a.length > 80) {
      setError("Please enter your name (max 80 chars).");
      return;
    }
    if (t.length === 0 || t.length > 1000) {
      setError("Please enter a comment (max 1000 chars).");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`/api/blog/${encodeURIComponent(postId)}/comments`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ author: a, text: t, website: w, honeypot }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Request failed: ${res.status}`);
      }
      const json = await res.json();
      if (json && json.comment) {
        setComments((prev) => [...prev, json.comment]);
        setAuthor("");
        setWebsite("");
        setText("");
        setHoneypot("");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to post comment");
    } finally {
      setSubmitting(false);
    }
  }

  async function removeComment(id: string) {
    if (!editMode) return;
    const pw = (adminPw || "").trim();
    if (!isLoggedIn && !pw) {
      setError("Sign in (or enter admin password) to delete comments.");
      return;
    }
    const ok = window.confirm("Delete this comment? This cannot be undone.");
    if (!ok) return;
    setError(null);
    setDeletingId(id);
    try {
      const res = await fetch(`/api/blog/${encodeURIComponent(postId)}/comments`, {
        method: "DELETE",
        headers: { "content-type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ password: pw, commentId: id }),
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || `Request failed: ${res.status}`);
      }
      setComments((prev) => prev.filter((c) => c.id !== id));
    } catch (err: any) {
      setError(err?.message || "Failed to delete comment");
    } finally {
      setDeletingId(null);
    }
  }

  function fmtDate(iso: string): string {
    try {
      const d = new Date(iso);
      if (!Number.isFinite(d.getTime())) return "";
      return d.toLocaleString();
    } catch {
      return "";
    }
  }

  return (
    <div className="comments">
      <button className="c-toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-controls={`comments-${postId}`}>
        {open ? "Hide" : "Show"} Comments ({comments.length})
      </button>
      {open && (
        <div id={`comments-${postId}`} className="c-panel" role="region" aria-label="Post comments">
          {loading ? (
            <div className="c-loading">Loading comments…</div>
          ) : (
            <>
              {comments.length === 0 ? (
                <div className="c-empty">No comments yet. Be the first to comment.</div>
              ) : (
                <ul className="c-list">
                  {comments.map((c) => (
                    <li key={c.id} className="c-item">
                      <div className="c-meta">
                        <span className="c-author">{c.author}</span>
                        <span className="c-date">{fmtDate(c.createdAt)}</span>
                        {editMode && (
                          <button
                            type="button"
                            className="btn btn-danger c-del"
                            onClick={() => removeComment(c.id)}
                            disabled={deletingId === c.id}
                            title="Delete comment"
                          >
                            {deletingId === c.id ? "Deleting…" : "Delete"}
                          </button>
                        )}
                      </div>
                      <div className="c-text" title={c.text}>{c.text}</div>
                      {c.website && (
                        <div className="c-website">
                          <a href={c.website} target="_blank" rel="noopener noreferrer">
                            {c.website}
                          </a>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              <form className="c-form" onSubmit={submit}>
                <div className="c-row">
                  <label className="c-label">Name</label>
                  <input className="c-input" type="text" value={author} maxLength={80} onChange={(e) => setAuthor(e.target.value)} required />
                </div>
                <div className="c-row">
                  <label className="c-label">Website (optional)</label>
                  <input className="c-input" type="url" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://example.com" />
                </div>
                {/* Honeypot field: hidden from real users */}
                <div className="c-hp">
                  <label>Company</label>
                  <input type="text" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
                </div>
                <div className="c-row">
                  <label className="c-label">Comment</label>
                  <textarea className="c-textarea" value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={1000} required />
                </div>
                {error && <div className="c-error" role="alert">{error}</div>}
                <div className="c-actions">
                  <button className="btn btn-primary" type="submit" disabled={submitting}>
                    {submitting ? "Posting…" : "Post Comment"}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      )}

      <style>
        {`
          .comments { margin-top: 10px; }
          .c-toggle {
            border: 2px solid #000;
            box-shadow: 3px 3px 0 #000;
            background: #fff;
            padding: 6px 10px;
            cursor: pointer;
          }
          .c-panel {
            margin-top: 8px;
            border: 2px solid #000;
            background: #f8f8f8;
            box-shadow: 4px 4px 0 #000;
            padding: 10px;
          }
          .c-loading, .c-empty { font-style: italic; }
          .c-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 10px; }
          .c-item { background: #fff; border: 2px solid #000; box-shadow: 3px 3px 0 #000; padding: 8px; }
          .c-meta { display: flex; gap: 10px; font-size: 0.9rem; margin-bottom: 6px; }
          .c-author { font-weight: 800; }
          .c-date { color: #333; opacity: 0.8; }
          .c-del { margin-left: auto; }
          .c-text { white-space: pre-wrap; line-height: 1.5; }
          .c-website a { word-break: break-all; }
          .c-form { margin-top: 12px; display: grid; gap: 8px; }
          .c-row { display: grid; gap: 4px; }
          .c-label { font-weight: 700; }
          .c-input, .c-textarea {
            border: 2px solid #000;
            padding: 6px 8px;
            font: inherit;
            background: #fff;
          }
          .c-error { color: #b00020; font-weight: 700; }
          .c-actions { margin-top: 4px; }
          .c-hp { position: absolute; left: -99999px; width: 1px; height: 1px; overflow: hidden; }
        `}
      </style>
    </div>
  );
};

export default Comments;
